import { c as getApiBaseUrl, l as getAuthToken, o as softParseStandard, s as withGgeGatewayPath } from "./validate.js";
import { A as importRequestResponseSchema, An as deleteAttachmentDescriptionResponseSchema, Bn as createResponseSchema, E as listResponseSchema, En as deleteRemarkResponseSchema, F as getVersionsResponseSchema, Ht as getHistoryResponseSchema, Jn as bindResponseSchema, Pn as createRemarkResponseSchema, Rt as getNeedsAttentionResponseSchema, S as listAttachmentDescriptionsResponseSchema, an as getFilterValuesResponseSchema, f as transitionResponseSchema, jt as getRefusalResponseSchema, ln as getCurrentVersionResponseSchema, pn as getCountsResponseSchema, r as updateResponseSchema, s as unlinkResponseSchema, v as setAttachmentDescriptionResponseSchema, vn as get2ResponseSchema, wt as getRequestResponseSchema, xn as exportRequestsResponseSchema, xt as getRequestsResponseSchema } from "./updateSchema.js";
import axios from "axios";
//#region src/__generated__/internal/application/.kubb/serializers.ts
function isFormBody(body) {
	return body instanceof FormData || body instanceof URLSearchParams || body instanceof Blob || body instanceof ArrayBuffer || ArrayBuffer.isView(body) || typeof body === "string";
}
function isDefaultJsonBody(body) {
	return body !== void 0 && body !== null && !isFormBody(body);
}
function appendFormDataValue({ formData, key, value, contentType }) {
	if (value === void 0 || value === null) return;
	if (value instanceof Blob) formData.append(key, value);
	else if (typeof value === "object" && !(value instanceof Date)) {
		const json = JSON.stringify(value);
		formData.append(key, contentType ? new Blob([json], { type: contentType }) : json);
	} else formData.append(key, toValue(value));
}
/**
* Default body serializer: passes binary/form bodies through and JSON-serializes everything else.
* For `multipart/form-data` plain objects become `FormData` and for
* `application/x-www-form-urlencoded` they become `URLSearchParams`. When `encoding` is supplied each
* urlencoded property follows its OpenAPI `style` / `explode` / `allowReserved`.
*
* @example
* ```ts
* defaultBodySerializer({ body: { name: 'odie' } }) // '{"name":"odie"}'
* defaultBodySerializer({ body: { field: 'x' }, contentType: 'multipart/form-data' }) // FormData
* defaultBodySerializer({ body: { tags: ['a', 'b'] }, contentType: 'application/x-www-form-urlencoded', encoding: { tags: { explode: false } } }) // 'tags=a,b'
* defaultBodySerializer({ body: { meta: { a: 1 } }, contentType: 'multipart/form-data', encoding: { meta: { contentType: 'application/json' } } }) // FormData with a typed Blob part
* ```
*/
const defaultBodySerializer = ({ body, contentType, encoding }) => {
	if (body === void 0 || body === null) return void 0;
	if (isFormBody(body)) return body;
	if (contentType?.includes("multipart/form-data")) {
		const formData = new FormData();
		for (const [key, value] of Object.entries(body)) {
			const partContentType = encoding?.[key]?.contentType;
			if (Array.isArray(value)) for (const item of value) appendFormDataValue({
				formData,
				key,
				value: item,
				contentType: partContentType
			});
			else appendFormDataValue({
				formData,
				key,
				value,
				contentType: partContentType
			});
		}
		return formData;
	}
	if (contentType?.includes("application/x-www-form-urlencoded")) {
		if (encoding) return serializeUrlencodedBody(body, encoding);
		return new URLSearchParams(body);
	}
	return JSON.stringify(body);
};
function serializeUrlencodedBody(body, encoding) {
	const parts = [];
	for (const [key, value] of Object.entries(body)) {
		const propertyEncoding = encoding[key];
		parts.push(...propertyEncoding ? serializeStyledQueryParam({
			key,
			value,
			options: propertyEncoding
		}) : serializeDefaultQueryParam(key, value));
	}
	return parts.join("&");
}
function serializeCookie({ name, value, explode }) {
	if (Array.isArray(value)) {
		const items = value.filter(notNullish).map((item) => encodeURIComponent(toValue(item)));
		return explode ? items.map((item) => `${name}=${item}`).join("; ") : `${name}=${items.join(",")}`;
	}
	if (isRecord(value)) {
		const entries = Object.entries(value).filter(([, item]) => notNullish(item));
		if (explode) return entries.map(([key, item]) => `${key}=${encodeURIComponent(toValue(item))}`).join("; ");
		return `${name}=${entries.flatMap(([key, item]) => [key, item]).map((item) => encodeURIComponent(toValue(item))).join(",")}`;
	}
	return `${name}=${encodeURIComponent(toValue(value))}`;
}
/**
* Serializes cookie parameters into a `Cookie` header value using the OpenAPI `form` style, joined
* with `; `. Values are URL-encoded and `explode` is honored per parameter.
*
* @example
* ```ts
* serializeCookies({ session: 'abc', ids: [1, 2] }) // 'session=abc; ids=1,2'
* serializeCookies({ ids: [1, 2] }, { ids: { explode: true } }) // 'ids=1; ids=2'
* ```
*/
function serializeCookies(cookies, styles) {
	const parts = [];
	for (const [name, value] of Object.entries(cookies)) {
		if (value === void 0 || value === null) continue;
		parts.push(serializeCookie({
			name,
			value,
			explode: styles?.[name]?.explode ?? false
		}));
	}
	return parts.join("; ");
}
function appendQueryValue({ search, key, value }) {
	if (value === void 0 || value === null) return;
	if (Array.isArray(value)) {
		for (const item of value) appendQueryValue({
			search,
			key,
			value: item
		});
		return;
	}
	if (isRecord(value)) {
		for (const [prop, propValue] of Object.entries(value)) appendQueryValue({
			search,
			key: `${key}[${prop}]`,
			value: propValue
		});
		return;
	}
	search.append(key, toValue(value));
}
const queryDelimiters = {
	form: ",",
	spaceDelimited: "%20",
	pipeDelimited: "|",
	deepObject: ","
};
function notNullish(value) {
	return value !== void 0 && value !== null;
}
/**
* Renders a primitive parameter value as a string, serializing `Date` to ISO-8601 so dates are
* stable across path, query, cookie, and header locations rather than locale-dependent.
*/
function toValue(value) {
	return value instanceof Date ? value.toISOString() : String(value);
}
/**
* Percent-encodes a value, keeping RFC 3986 reserved characters intact (used when `allowReserved` is set).
*/
function encodeReserved(value) {
	return encodeURI(toValue(value));
}
/**
* Percent-encodes a value, escaping reserved characters (the default query/path encoder).
*/
function encodeComponent(value) {
	return encodeURIComponent(toValue(value));
}
/**
* Whether a value should expand into bracketed/keyed parts. Arrays and `Date` are excluded so they
* are serialized as a unit (a `Date` becomes an ISO string, not its enumerable own properties).
*/
function isRecord(value) {
	return typeof value === "object" && value !== null && !Array.isArray(value) && !(value instanceof Date);
}
/**
* Expands an object or array into `deepObject` query parts, recursing into nested values so
* `{ a: { b: { c: 1 } } }` becomes `a[b][c]=1`. Primitives terminate the recursion.
*/
function serializeDeepObject({ key, value, encode }) {
	if (value === void 0 || value === null) return [];
	if (Array.isArray(value)) return value.flatMap((item, index) => serializeDeepObject({
		key: `${key}[${index}]`,
		value: item,
		encode
	}));
	if (isRecord(value)) return Object.entries(value).filter(([, item]) => notNullish(item)).flatMap(([prop, item]) => serializeDeepObject({
		key: `${key}[${prop}]`,
		value: item,
		encode
	}));
	return [`${encode(key)}=${encode(value)}`];
}
function serializeStyledQueryArray({ key, value, options, encode }) {
	const items = value.filter(notNullish);
	if (options.explode ?? true) return items.map((item) => `${encode(key)}=${encode(item)}`);
	return [`${encode(key)}=${items.map(encode).join(queryDelimiters[options.style ?? "form"])}`];
}
function serializeStyledQueryObject({ key, value, options, encode }) {
	if ((options.style ?? "form") === "deepObject") return serializeDeepObject({
		key,
		value,
		encode
	});
	const entries = Object.entries(value).filter(([, item]) => notNullish(item));
	if (options.explode ?? true) return entries.map(([prop, item]) => `${encode(prop)}=${encode(item)}`);
	return [`${encode(key)}=${entries.flatMap(([prop, item]) => [prop, item]).map(encode).join(",")}`];
}
function serializeStyledQueryParam({ key, value, options }) {
	if (value === void 0 || value === null) return [];
	const encode = options.allowReserved ? encodeReserved : encodeComponent;
	if (Array.isArray(value)) return serializeStyledQueryArray({
		key,
		value,
		options,
		encode
	});
	if (isRecord(value)) return serializeStyledQueryObject({
		key,
		value,
		options,
		encode
	});
	return [`${encode(key)}=${encode(value)}`];
}
function serializeDefaultQueryParam(key, value) {
	const search = new URLSearchParams();
	appendQueryValue({
		search,
		key,
		value
	});
	const result = search.toString();
	return result ? [result] : [];
}
/**
* Default query serializer. Members with `options` metadata follow their OpenAPI `style` / `explode`
* / `allowReserved`. Members without it keep the defaults: arrays explode into repeated keys and
* nested objects use the `deepObject` style (`key[prop]=value`).
*
* @example
* ```ts
* defaultQuerySerializer({ id: [3, 4, 5] }) // 'id=3&id=4&id=5'
* defaultQuerySerializer({ id: [3, 4, 5] }, { id: { style: 'form', explode: false } }) // 'id=3,4,5'
* defaultQuerySerializer({ id: [3, 4, 5] }, { id: { style: 'spaceDelimited', explode: false } }) // 'id=3%204%205'
* defaultQuerySerializer({ id: [3, 4, 5] }, { id: { style: 'pipeDelimited', explode: false } }) // 'id=3|4|5'
* defaultQuerySerializer({ a: { b: 1 } }, { a: { style: 'deepObject' } }) // 'a%5Bb%5D=1'
* defaultQuerySerializer({ a: { b: { c: 1 } } }, { a: { style: 'deepObject' } }) // 'a%5Bb%5D%5Bc%5D=1'
* ```
*/
const defaultQuerySerializer = (params, options) => {
	const parts = [];
	for (const [key, value] of Object.entries(params)) {
		const paramOptions = options?.[key];
		parts.push(...paramOptions ? serializeStyledQueryParam({
			key,
			value,
			options: paramOptions
		}) : serializeDefaultQueryParam(key, value));
	}
	return parts.join("&");
};
function serializePathPrimitive({ name, value, style }) {
	const encoded = encodeComponent(value);
	if (style === "label") return `.${encoded}`;
	if (style === "matrix") return `;${name}=${encoded}`;
	return encoded;
}
function serializePathArray({ name, value, style, explode }) {
	const items = value.map(encodeComponent);
	if (style === "label") return `.${items.join(explode ? "." : ",")}`;
	if (style === "matrix") return explode ? items.map((item) => `;${name}=${item}`).join("") : `;${name}=${items.join(",")}`;
	return items.join(",");
}
function serializePathObject({ name, value, style, explode }) {
	const members = Object.entries(value).map(([key, item]) => explode ? `${encodeComponent(key)}=${encodeComponent(item)}` : `${encodeComponent(key)},${encodeComponent(item)}`);
	if (style === "label") return `.${members.join(explode ? "." : ",")}`;
	if (style === "matrix") return explode ? members.map((member) => `;${member}`).join("") : `;${name}=${members.join(",")}`;
	return members.join(",");
}
/**
* Default path serializer honoring the OpenAPI `style` / `explode` metadata. Without metadata it
* falls back to `simple` style with `explode: false`. Replaces the previous `String(value)`
* interpolation, which emitted `[object Object]` for object path params.
*
* @example
* ```ts
* defaultPathSerializer({ name: 'id', value: [3, 4, 5] }) // '3,4,5'
* defaultPathSerializer({ name: 'id', value: [3, 4, 5], options: { style: 'label', explode: true } }) // '.3.4.5'
* defaultPathSerializer({ name: 'id', value: [3, 4, 5], options: { style: 'matrix', explode: true } }) // ';id=3;id=4;id=5'
* defaultPathSerializer({ name: 'pt', value: { x: 1, y: 2 } }) // 'x,1,y,2'
* ```
*/
const defaultPathSerializer = ({ name, value, options }) => {
	if (value === void 0 || value === null) return "";
	const style = options?.style ?? "simple";
	const explode = options?.explode ?? false;
	if (Array.isArray(value)) return serializePathArray({
		name,
		value,
		style,
		explode
	});
	if (isRecord(value)) return serializePathObject({
		name,
		value,
		style,
		explode
	});
	return serializePathPrimitive({
		name,
		value,
		style
	});
};
function serializeHeaderValue(value, explode) {
	if (Array.isArray(value)) return value.filter(notNullish).map(toValue).join(",");
	if (!isRecord(value)) return toValue(value);
	const entries = Object.entries(value).filter(([, item]) => notNullish(item));
	if (explode) return entries.map(([key, item]) => `${key}=${toValue(item)}`).join(",");
	return entries.flatMap(([key, item]) => [key, item]).map(toValue).join(",");
}
/**
* Serializes array and object header parameters with the OpenAPI `simple` style before they are
* merged. Header values are not URL-encoded. Primitive values and headers without metadata pass
* through untouched.
*/
function applyHeaderStyles(headers, styles) {
	if (!headers || !styles) return headers;
	return (Array.isArray(headers) ? headers : Object.entries(headers)).map(([key, value]) => {
		const style = styles[key];
		if (!style || value === void 0 || value === null || typeof value !== "object") return [key, value];
		return [key, serializeHeaderValue(value, style.explode ?? false)];
	});
}
//#endregion
//#region src/__generated__/internal/application/.kubb/standardSchema.ts
/**
* Thrown by `validateStandardSchema` when validation fails. Carries the raw `issues` array from
* the schema's `validate` result so callers receive a uniform error shape regardless of which
* schema library is in use.
*/
var ParseError = class extends Error {
	issues;
	constructor({ issues, message }) {
		super(message ?? "Validation failed");
		this.name = "ParseError";
		this.issues = issues;
	}
};
/**
* Validates `value` against a Standard Schema-compatible `schema`. Returns the parsed output on
* success; throws `ParseError` with the schema's `issues` on failure. Handles both sync and async
* `validate` implementations transparently.
*
* @example
* const pet = await validateStandardSchema(PetSchema, rawData)
*/
async function validateStandardSchema(schema, value) {
	const result = await schema["~standard"].validate(value);
	if (result.issues) throw new ParseError({ issues: result.issues });
	return result.value;
}
//#endregion
//#region src/__generated__/internal/application/.kubb/client.ts
/**
* Thrown for a non-2xx response, so a resolved call always means success.
*/
var ResponseError = class extends Error {
	data;
	status;
	statusText;
	contentType;
	request;
	response;
	constructor(config) {
		super(`Request failed with status ${config.status}${config.statusText ? ` ${config.statusText}` : ""}`);
		this.name = "ResponseError";
		this.data = config.data;
		this.status = config.status;
		this.statusText = config.statusText;
		this.contentType = config.contentType;
		this.request = config.request;
		this.response = config.response;
	}
};
function serializeHeaders(headers) {
	if (!headers) return {};
	const entries = Array.isArray(headers) ? headers : Object.entries(headers);
	const result = {};
	for (const [key, value] of entries) {
		if (value === void 0 || value === null) continue;
		result[key] = typeof value === "string" ? value : typeof value === "object" ? JSON.stringify(value) : String(value);
	}
	return result;
}
function mergeHeaders(...sources) {
	return Object.assign({}, ...sources.map(serializeHeaders));
}
function getHeader(headers, name) {
	const key = Object.keys(headers).find((k) => k.toLowerCase() === name.toLowerCase());
	return key ? headers[key] : void 0;
}
function hasHeader(headers, name) {
	return Object.keys(headers).some((k) => k.toLowerCase() === name.toLowerCase());
}
/**
* Joins the URL parts, interpolates URL-encoded `{param}` segments, and appends the serialized query, backing `getUrl`.
*/
function serializeUrl({ parts, pathParams, search, pathSerializer = defaultPathSerializer, pathStyles }) {
	return parts.filter(Boolean).join("").replace(/\{([^{}]+)\}/g, (_, key) => pathSerializer({
		name: key,
		value: pathParams[key],
		options: pathStyles?.[key]
	})) + (search ? `?${search}` : "");
}
/**
* Wraps an axios interceptor registration behind the shared `use` / `eject` / `update` API, mapping a stable external id onto axios's own so `update` can swap a handler in place.
*/
function createInterceptorChannel(register, ejectNative) {
	const ids = /* @__PURE__ */ new Map();
	let counter = 0;
	return {
		use(fn) {
			const id = ++counter;
			ids.set(id, register(fn));
			return id;
		},
		eject(id) {
			const nativeId = ids.get(id);
			if (nativeId === void 0) return;
			ejectNative(nativeId);
			ids.delete(id);
		},
		update(id, fn) {
			const nativeId = ids.get(id);
			if (nativeId !== void 0) ejectNative(nativeId);
			ids.set(id, register(fn));
		}
	};
}
/**
* Walks the per-operation security in order and places the first resolved token on the request, mutating `headers` / `query` in place.
*/
async function resolveAuth(params) {
	const { security, auth, headers, query } = params;
	if (!security?.length || auth === void 0) return;
	for (const scheme of security) {
		const token = typeof auth === "function" ? await auth(scheme) : auth;
		if (token === void 0) continue;
		if (scheme.type === "apiKey") {
			const name = scheme.name ?? "Authorization";
			if (scheme.in === "query") {
				if (query[name] === void 0) query[name] = token;
			} else if (scheme.in === "cookie") headers.Cookie = [headers.Cookie, `${name}=${token}`].filter(Boolean).join("; ");
			else if (!hasHeader(headers, name)) headers[name] = token;
		} else if (!hasHeader(headers, "Authorization")) headers.Authorization = scheme.scheme === "basic" ? `Basic ${btoa(token)}` : `Bearer ${token}`;
		return;
	}
}
async function runValidator(validator, value) {
	if (!validator) return value;
	return validateStandardSchema(validator, value);
}
/**
* The base media type of a `Content-Type` value, lowercased and stripped of any `; charset=...` parameters.
*/
function baseContentType(value) {
	if (!value) return void 0;
	return value.split(";")[0].trim().toLowerCase() || void 0;
}
/**
* Reads the negotiated response content type from the response headers as a base media type.
*/
function getResponseContentType(headers) {
	if (!headers) return void 0;
	const value = headers["content-type"] ?? headers["Content-Type"];
	return baseContentType(typeof value === "string" ? value : void 0);
}
/**
* Normalizes the `contentType` option to its `{ request, response }` form, treating a bare string as the request content type.
*/
function resolveContentType(contentType) {
	if (typeof contentType === "string") return { request: contentType };
	return contentType ?? {};
}
/**
* The per-concern serializers for a call, the per-call serializer winning over the client's and
* falling back to the defaults.
*/
function resolveSerializers({ config, requestConfig }) {
	return {
		querySerializer: requestConfig.serializer?.query ?? config.serializer?.query ?? defaultQuerySerializer,
		bodySerializer: requestConfig.serializer?.body ?? config.serializer?.body ?? defaultBodySerializer,
		pathSerializer: requestConfig.serializer?.path ?? config.serializer?.path ?? defaultPathSerializer
	};
}
/**
* Resolves everything a call needs before it touches axios: merged headers with the negotiated
* content type, auth on headers or query, serialized cookies, the validated and serialized body,
* and the final axios request config with `throwOnError` riding `validateStatus`.
*/
async function resolveRequest({ config, requestConfig }) {
	const { querySerializer, bodySerializer, pathSerializer } = resolveSerializers({
		config,
		requestConfig
	});
	const codecs = {
		...config.codecs,
		...requestConfig.codecs
	};
	const headers = mergeHeaders(config.headers, applyHeaderStyles(requestConfig.headers, requestConfig.styles?.header));
	const { request: requestContentTypeOption, response: responseContentType } = resolveContentType(requestConfig.contentType);
	const requestContentType = requestContentTypeOption ?? getHeader(headers, "content-type");
	if (responseContentType && !hasHeader(headers, "accept")) headers["Accept"] = responseContentType;
	const query = { ...requestConfig.query ?? requestConfig.params };
	await resolveAuth({
		security: requestConfig.security,
		auth: requestConfig.auth ?? config.auth,
		headers,
		query
	});
	const authToken = getAuthToken();
	if (authToken && !hasHeader(headers, "authorization")) headers["Authorization"] = `Bearer ${authToken}`;
	if (requestConfig.cookies) {
		const cookie = serializeCookies(requestConfig.cookies, requestConfig.styles?.cookie);
		if (cookie) headers.Cookie = [headers.Cookie, cookie].filter(Boolean).join("; ");
	}
	const validatedBody = await runValidator(requestConfig.validator?.request, requestConfig.body);
	const requestContentTypeBase = baseContentType(requestContentType);
	const contentCodec = requestContentTypeBase ? codecs[requestContentTypeBase] : void 0;
	const usesDefaultBodySerializer = !contentCodec?.serialize && bodySerializer === defaultBodySerializer;
	const body = contentCodec?.serialize ? contentCodec.serialize(validatedBody, requestContentType) : bodySerializer({
		body: validatedBody,
		contentType: requestContentType,
		encoding: requestConfig.styles?.body
	});
	if (body instanceof FormData) {
		for (const key of Object.keys(headers)) if (key.toLowerCase() === "content-type") delete headers[key];
	} else if (requestContentTypeOption) headers["Content-Type"] = requestContentTypeOption;
	else if (usesDefaultBodySerializer && isDefaultJsonBody(validatedBody) && !hasHeader(headers, "content-type")) headers["Content-Type"] = "application/json";
	const pathParams = requestConfig.path ?? {};
	const url = withGgeGatewayPath((requestConfig.url ?? "").replace(/\{([^{}]+)\}/g, (_, key) => pathSerializer({
		name: key,
		value: pathParams[key],
		options: requestConfig.styles?.path?.[key]
	})));
	const throwOnError = requestConfig.throwOnError ?? config.throwOnError ?? true;
	const validateStatus = requestConfig.validateStatus ?? config.validateStatus ?? (throwOnError ? (status) => status >= 200 && status < 300 : () => true);
	const axiosConfig = {
		...config.options || requestConfig.options ? {
			...config.options,
			...requestConfig.options
		} : void 0,
		url,
		baseURL: requestConfig.baseURL ?? config.baseURL ?? getApiBaseUrl(),
		method: requestConfig.method ?? "GET",
		headers,
		params: query,
		paramsSerializer: (params) => querySerializer(params, requestConfig.styles?.query),
		data: body,
		transformRequest: (data) => data,
		signal: requestConfig.signal,
		responseType: requestConfig.responseType,
		validateStatus
	};
	if (requestConfig.responseType === "stream" && !axiosConfig.adapter) axiosConfig.adapter = "fetch";
	return {
		axiosConfig,
		codecs,
		throwOnError
	};
}
/**
* Turns an axios response into the call result: decodes the body through the matching codec and
* validates the success or error body. A thrown axios error never reaches this, so `error` here is
* always the body of a non-2xx response that `validateStatus` let through.
*/
async function settleResponse({ response, codecs, validator }) {
	const isSuccess = response.status >= 200 && response.status < 300;
	const contentType = getResponseContentType(response.headers);
	let decoded = response.data;
	if (contentType) {
		const codec = codecs[contentType];
		if (codec?.deserialize) decoded = await codec.deserialize(response.data, contentType);
	}
	const data = isSuccess ? await softParseStandard(validator?.response, decoded, `${response.config?.method ?? "?"} ${response.config?.url ?? "?"}`) : void 0;
	const error = isSuccess ? void 0 : await runValidator(validator?.error, decoded);
	return {
		status: response.status,
		data,
		error,
		contentType,
		request: response.config,
		response
	};
}
/**
* Builds the shared client core bound to an axios instance (defaulting to `axios.create()`), with `throwOnError` riding axios's `validateStatus`.
*/
function createClientCore(options = {}) {
	let config = { ...options };
	const instance = config.transport ?? axios.create();
	const requestManager = instance.interceptors.request;
	const responseManager = instance.interceptors.response;
	const interceptors = {
		request: createInterceptorChannel((fn) => requestManager.use(fn), (id) => requestManager.eject(id)),
		response: createInterceptorChannel((fn) => responseManager.use(fn), (id) => responseManager.eject(id)),
		error: createInterceptorChannel((fn) => responseManager.use(void 0, async (error) => {
			await fn(error);
			return Promise.reject(error);
		}), (id) => responseManager.eject(id))
	};
	const client = (async (requestConfig) => {
		const activeInstance = requestConfig.transport ?? config.transport ?? instance;
		const { axiosConfig, codecs, throwOnError } = await resolveRequest({
			config,
			requestConfig
		});
		try {
			return await settleResponse({
				response: await activeInstance.request(axiosConfig),
				codecs,
				validator: requestConfig.validator
			});
		} catch (error) {
			const axiosError = error;
			if (throwOnError && axiosError.response) throw new ResponseError({
				data: axiosError.response.data,
				status: axiosError.response.status,
				statusText: axiosError.response.statusText,
				contentType: getResponseContentType(axiosError.response.headers),
				request: axiosError.config,
				response: axiosError.response
			});
			throw error;
		}
	});
	client.getConfig = () => config;
	client.setConfig = (next) => {
		config = {
			...config,
			...next,
			headers: {
				...serializeHeaders(config.headers),
				...serializeHeaders(next.headers)
			}
		};
		return config;
	};
	client.getUrl = (requestConfig) => {
		const { querySerializer, pathSerializer } = resolveSerializers({
			config,
			requestConfig
		});
		const query = { ...requestConfig.query ?? requestConfig.params };
		return serializeUrl({
			parts: [requestConfig.baseURL ?? config.baseURL, requestConfig.url],
			pathParams: requestConfig.path ?? {},
			search: querySerializer(query, requestConfig.styles?.query),
			pathSerializer,
			pathStyles: requestConfig.styles?.path
		});
	};
	client.interceptors = interceptors;
	client.createClient = (next) => createClientCore({
		...config,
		...next
	});
	return client;
}
async function* readBytes(stream) {
	if (!("getReader" in stream)) {
		yield* stream;
		return;
	}
	const reader = stream.getReader();
	try {
		while (true) {
			const { done, value } = await reader.read();
			if (done) return;
			yield value;
		}
	} finally {
		await reader.cancel().catch(() => {});
	}
}
function parseEvent(raw) {
	const data = [];
	const event = { data: void 0 };
	let seen = false;
	for (const line of raw.split("\n")) {
		if (!line || line.startsWith(":")) continue;
		seen = true;
		const index = line.indexOf(":");
		const field = index === -1 ? line : line.slice(0, index);
		const value = index === -1 ? "" : line.slice(index + 1).replace(/^ /, "");
		if (field === "data") data.push(value);
		else if (field === "event") event.event = value;
		else if (field === "id") event.id = value;
		else if (field === "retry" && Number.isFinite(Number(value))) event.retry = Number(value);
	}
	if (!seen) return void 0;
	if (data.length) {
		const joined = data.join("\n");
		try {
			event.data = JSON.parse(joined);
		} catch {
			event.data = joined;
		}
	}
	return event;
}
/**
* Parses a `text/event-stream` body into typed Server-Sent Events, consumed with `for await` and stopped early by breaking the loop.
*/
async function* parseEventStream(stream) {
	const decoder = new TextDecoder();
	const normalize = (text) => text.replace(/\r\n|\r/g, "\n");
	let buffer = "";
	for await (const chunk of readBytes(stream)) {
		const blocks = normalize(buffer + decoder.decode(chunk, { stream: true })).split("\n\n");
		buffer = blocks.pop() ?? "";
		for (const block of blocks) {
			const event = parseEvent(block);
			if (event) yield event;
		}
	}
	const event = parseEvent(normalize(buffer + decoder.decode()));
	if (event) yield event;
}
/**
* Wraps a transport result whose `data` is a streaming body into an `EventStreamResult`, exposing
* the parsed events as a typed async iterator. Generated SSE operations call this.
*/
async function toEventStream(result) {
	const { data, response } = await result;
	return {
		response,
		stream: parseEventStream(data)
	};
}
const client = createClientCore();
const createClient = (config) => client.createClient(config);
//#endregion
//#region src/__generated__/internal/application/client/bind.ts
/**
* @summary Привязать актуальную часть ТУ к подразделению
* {@link /api/mdm/internal/api/internal/requests/:requestId/tu-parts/:departmentId/:fileId}
*/
function bind(options) {
	const { client: request = client, ...config } = options;
	return request({
		method: "PUT",
		url: "/api/mdm/internal/api/internal/requests/{requestId}/tu-parts/{departmentId}/{fileId}",
		validator: { response: bindResponseSchema },
		...config
	});
}
//#endregion
//#region src/__generated__/internal/application/client/create.ts
/**
* @summary Создать черновик итогового решения
* {@link /api/mdm/internal/api/internal/requests/:requestId/final-decision}
*/
function create(options) {
	const { client: request = client, ...config } = options;
	return request({
		method: "POST",
		url: "/api/mdm/internal/api/internal/requests/{requestId}/final-decision",
		validator: { response: createResponseSchema },
		...config
	});
}
//#endregion
//#region src/__generated__/internal/application/client/createRemark.ts
/**
* @summary Добавить замечание к текущей версии
* {@link /api/mdm/internal/api/internal/requests/:requestId/final-decision/current-version/remarks}
*/
function createRemark(options) {
	const { client: request = client, ...config } = options;
	return request({
		method: "POST",
		url: "/api/mdm/internal/api/internal/requests/{requestId}/final-decision/current-version/remarks",
		validator: { response: createRemarkResponseSchema },
		...config
	});
}
//#endregion
//#region src/__generated__/internal/application/client/deleteAttachmentDescription.ts
/**
* @description Только OPERATOR, запрещено если заявка финализирована.
* @summary Удалить описание вложения
* {@link /api/mdm/internal/api/internal/requests/:id/attachments/:fileId}
*/
function deleteAttachmentDescription(options) {
	const { client: request = client, ...config } = options;
	return request({
		method: "DELETE",
		url: "/api/mdm/internal/api/internal/requests/{id}/attachments/{fileId}",
		validator: { response: deleteAttachmentDescriptionResponseSchema },
		...config
	});
}
//#endregion
//#region src/__generated__/internal/application/client/deleteRemark.ts
/**
* @summary Логически удалить своё замечание к текущей версии
* {@link /api/mdm/internal/api/internal/requests/:requestId/final-decision/current-version/remarks/:remarkId}
*/
function deleteRemark(options) {
	const { client: request = client, ...config } = options;
	return request({
		method: "DELETE",
		url: "/api/mdm/internal/api/internal/requests/{requestId}/final-decision/current-version/remarks/{remarkId}",
		validator: { response: deleteRemarkResponseSchema },
		...config
	});
}
//#endregion
//#region src/__generated__/internal/application/client/exportRequests.ts
/**
* @description         Формирует один ZIP-архив: на каждую заявку — папка request_<id>
* (outcome.json, comments.json и файлы результата). Доступно только OPERATOR.
* Только для заявок в статусе REVISION_REQUIRED/TU_ISSUED/REJECTED. Fail-fast: любая невалидная
* заявка отменяет весь экспорт (ничего не стримится).
* @summary Пакетный экспорт результата заявок
* {@link /api/mdm/internal/api/internal/requests/export}
*/
function exportRequests(options) {
	const { client: request = client, ...config } = options;
	return request({
		method: "POST",
		url: "/api/mdm/internal/api/internal/requests/export",
		validator: { response: exportRequestsResponseSchema },
		...config
	});
}
//#endregion
//#region src/__generated__/internal/application/client/get2.ts
/**
* @summary Получить черновик итогового решения
* {@link /api/mdm/internal/api/internal/requests/:requestId/final-decision}
*/
function get2(options) {
	const { client: request = client, ...config } = options;
	return request({
		method: "GET",
		url: "/api/mdm/internal/api/internal/requests/{requestId}/final-decision",
		validator: { response: get2ResponseSchema },
		...config
	});
}
//#endregion
//#region src/__generated__/internal/application/client/getCounts.ts
/**
* @description         Агрегат count(*) по статусам в скоупе роли: OPERATOR — все заявки;
* SECRETARY_NG и APPARATUS_NG — только заявки дороги, сопоставленной с дорогой
* пользователя в Keycloak; NTP_HEAD, NTP_RESPONSIBLE и прочие — только заявки
* со своим назначением (без назначений — нули).
* needsAttention = REVISION_REQUIRED (на доработке у заявителя).
* tiles — ролевые плашки: SECRETARY_NG — 5 (TOTAL/NEW/NEEDS_ATTENTION/AT_NG/AT_RAILWAY),
* APPARATUS_NG — 6 (TOTAL/SENT_TO_NG/IN_NG/IN_NTP/NG_APPROVAL/DECISION_REWORK),
* NTP_HEAD — 5 (TOTAL/SENT_TO_NTP/IN_NTP/NTP_APPROVAL/DECISION_REWORK),
* NTP_RESPONSIBLE — 5 (TOTAL/NTP_RESP_ASSIGNED/COMMISSION_PREP/DECISION_FORMING/DECISION_REWORK),
* OPERATOR — 4 (TOTAL/NEEDS_EXPORT/EXPORTED/LOADED),
* прочие роли — null.
* @summary Сводка-счётчики заявок по статусам
* {@link /api/mdm/internal/api/internal/requests/counts}
*/
function getCounts(options = {}) {
	const { client: request = client, ...config } = options;
	return request({
		method: "GET",
		url: "/api/mdm/internal/api/internal/requests/counts",
		validator: { response: getCountsResponseSchema },
		...config
	});
}
//#endregion
//#region src/__generated__/internal/application/client/getCurrentVersion.ts
/**
* @summary Получить текущую версию итогового решения
* {@link /api/mdm/internal/api/internal/requests/:requestId/final-decision/current-version}
*/
function getCurrentVersion(options) {
	const { client: request = client, ...config } = options;
	return request({
		method: "GET",
		url: "/api/mdm/internal/api/internal/requests/{requestId}/final-decision/current-version",
		validator: { response: getCurrentVersionResponseSchema },
		...config
	});
}
//#endregion
//#region src/__generated__/internal/application/client/getFilterValues.ts
/**
* @description Значения для выпадающих списков фильтров, которые нельзя составить
* заранее: станция/перегон (SECTION), заявитель (APPLICANT_NAME),
* инициатор (INITIATOR), ответственный (ASSIGNEE), оператор (OPERATOR).
* Возвращаются только значения, встречающиеся в видимых пользователю
* заявках — скоуп тот же, что у списка. Для колонок-людей value — id
* пользователя (его и надо слать в initiatorIds/assigneeIds/operatorIds),
* displayName — ФИО. «Не назначен» не приходит этим списком: для него в
* GET /api/internal/requests передаётся assigneeUnassigned=true
* (для «Оператора» — operatorUnassigned=true).
* Поиск q — подстрока без учёта регистра. Для станции, заявителя и
* инициатора — по отображаемому значению из БД. Для «Ответственного» и
* «Оператора» ФИО живёт в Keycloak, поэтому ищется по директории: фамилия, имя,
* логин или email (отчество не участвует), с последующим сужением до
* видимых заявок — размер выборки поиску не помеха.
* selected — уже выбранные значения, теми же строками, что пришли в value.
* Возвращаются всегда, вне q и limit.
* При переходе из мониторинга передавайте monitoringMetric, как в GET /requests:
* для НТП значения будут доступны по своей дороге вместо личных назначений.
* Значения ограничиваются областью доступа, а не выбранным показателем.
* @summary Доступные значения фильтра по колонке
* {@link /api/mdm/internal/api/internal/requests/filter-values}
*/
function getFilterValues(options) {
	const { client: request = client, ...config } = options;
	return request({
		method: "GET",
		url: "/api/mdm/internal/api/internal/requests/filter-values",
		validator: { response: getFilterValuesResponseSchema },
		...config
	});
}
//#endregion
//#region src/__generated__/internal/application/client/getHistory.ts
/**
* @description Журнал событий (eventType, actor, actorRole, payload, occurredAt),
* отсортированный по убыванию времени. Доступ как у detail: OPERATOR/MONITORING — любую;
* SECRETARY_NG и APPARATUS_NG — свою дорогу; NTP_HEAD — свои назначения или свою дорогу;
* NTP_RESPONSIBLE и прочие — только при наличии назначения на заявке.
* @summary История действий по заявке (F-INT-03)
* {@link /api/mdm/internal/api/internal/requests/:id/history}
*/
function getHistory(options) {
	const { client: request = client, ...config } = options;
	return request({
		method: "GET",
		url: "/api/mdm/internal/api/internal/requests/{id}/history",
		validator: { response: getHistoryResponseSchema },
		...config
	});
}
//#endregion
//#region src/__generated__/internal/application/client/getNeedsAttention.ts
/**
* @description Заявки, требующие действий текущего пользователя по его роли. Видимость:
* OPERATOR — все заявки; SECRETARY_NG и APPARATUS_NG — только заявки дороги,
* сопоставленной с дорогой пользователя в Keycloak; NTP_HEAD, NTP_RESPONSIBLE
* и прочие — только заявки со своим назначением.
* Заявитель отсутствует во внутреннем контуре; Секретарь НГ — INTAKE_CHECK;
* Аппарат НГ / НТП / Отв. НТП — статус ОБЯЗАН входить в фиксированный набор
* (Аппарат НГ: SENT_TO_NG/NG_APPROVAL; НТП: SENT_TO_NTP/NTP_APPROVAL;
* Отв. НТП: NTP_RESP_ASSIGNED/DECISION_REWORK) И пользователь ОБЯЗАН держать
* слот, которому принадлежит текущий статус заявки — оба условия одновременно;
* взятые в работу статусы (IN_NG, IN_NTP, COMMISSION_PREP, DECISION_FORMING)
* в очередь не входят, даже если пользователь держит их слот; Оператор —
* требующие выгрузки. Ролевые условия по статусу и держателю слота дополнительно
* пересекаются с указанной видимостью и не расширяют её.
* Роль без блока → пустой список. totalElements = число в заголовке блока.
* Поиск search — как в GET /requests: вхождение в название без учёта регистра и в number.
* Для роли MONITORING блока нет — возвращается пустой список.
* @summary Блок «Требует внимания»
* {@link /api/mdm/internal/api/internal/requests/needs-attention}
*/
function getNeedsAttention(options = {}) {
	const { client: request = client, ...config } = options;
	return request({
		method: "GET",
		url: "/api/mdm/internal/api/internal/requests/needs-attention",
		validator: { response: getNeedsAttentionResponseSchema },
		...config
	});
}
//#endregion
//#region src/__generated__/internal/application/client/getRefusal.ts
/**
* @summary Получить мотивированный отказ
* {@link /api/mdm/internal/api/internal/requests/:requestId/final-decision/refusal}
*/
function getRefusal(options) {
	const { client: request = client, ...config } = options;
	return request({
		method: "GET",
		url: "/api/mdm/internal/api/internal/requests/{requestId}/final-decision/refusal",
		validator: { response: getRefusalResponseSchema },
		...config
	});
}
//#endregion
//#region src/__generated__/internal/application/client/getRequest.ts
/**
* @description         Возвращает детальную информацию о заявке по идентификатору.
* OPERATOR/MONITORING — любую заявку; SECRETARY_NG и APPARATUS_NG — свою дорогу.
* NTP_HEAD — свои назначения, а также заявки своей дороги для переходов из мониторинга.
* NTP_RESPONSIBLE и прочие — только при наличии своего назначения.
* @summary Детали заявки (F-INT-03)
* {@link /api/mdm/internal/api/internal/requests/:id}
*/
function getRequest(options) {
	const { client: request = client, ...config } = options;
	return request({
		method: "GET",
		url: "/api/mdm/internal/api/internal/requests/{id}",
		validator: { response: getRequestResponseSchema },
		...config
	});
}
//#endregion
//#region src/__generated__/internal/application/client/getRequests.ts
/**
* @description         Возвращает пагинированный список заявок. Фильтры (AND): status, type,
* requestSubject, railway, applicantType, sections/applicantNames (точные
* значения из списка доступных), initiatorIds/assigneeIds/operatorIds
* (id пользователей), paymentFlag, processingFlag, deadlineControl, периоды
* importedFrom/To, processedFrom/To, createdFrom/To, updatedFrom/To (обе
* границы включительно; «конкретную дату» фронт присылает как границы своего
* дня) и reviewDueFrom/To (границы — ISO-даты, колонка хранит дату без времени).
* Дата обработки и контроль срока вычисляемые: фильтруются тем же выражением,
* которое показано в ячейке.
* Параметры section/applicantName/initiator (ILIKE-подстроки) устарели —
* используйте множественные аналоги.
* assigneeUnassigned=true добавляет заявки без ответственного; вместе с assigneeIds
* это объединение выбранных ответственных и нераспределённых заявок.
* operatorUnassigned=true аналогично добавляет заявки без оператора.
* Доступные значения фильтров: закрытые наборы — GET /api/internal/dictionaries,
* остальное — GET /api/internal/requests/filter-values.
* Поиск search — вхождение в название заявки (title, без учёта регистра)
* и number; поддерживается ввод №012345. Сортировка sortBy/sortDir (дефолт — imported_at DESC),
* вторичный ключ id. sortDir принимает строго ASC или DESC — значение
* регистрозависимо, ?sortDir=asc вернёт 400. sortDir без sortBy игнорируется;
* sortBy без sortDir — ASC. TYPE/STATUS/RAILWAY сортируются по отображаемому
* названию, текстовые поля — без учёта регистра. Видимость по роли: OPERATOR/MONITORING — все заявки;
* сортировка по ASSIGNEE использует имя из последнего назначения текущего слота,
* по OPERATOR — snapshot ФИО последнего операторского события;
* SECRETARY_NG и APPARATUS_NG — только заявки дороги, сопоставленной с дорогой
* пользователя в Keycloak; NTP_HEAD, NTP_RESPONSIBLE и прочие — только заявки
* со своим назначением. Для роли OPERATOR дополнительно заполняются
* processingFlag/receivedAt/processedAt/operator; какие из этого набора колонок
* показывать — решает фронт через настройки таблицы (контекст operator_requests).
* monitoringMetric включает область мониторинга: НТП и Аппарат НГ — своя дорога,
* Мониторинг — все дороги. Без monitoringMetric НТП видит только свои назначения.
* Для всей области мониторинга используйте monitoringMetric=TOTAL.
* Показатель, поиск, invoiceAmountFrom/To и прочие фильтры сочетаются через AND.
* @summary Список заявок (F-INT-03)
* {@link /api/mdm/internal/api/internal/requests}
*/
function getRequests(options = {}) {
	const { client: request = client, ...config } = options;
	return request({
		method: "GET",
		url: "/api/mdm/internal/api/internal/requests",
		validator: { response: getRequestsResponseSchema },
		...config
	});
}
//#endregion
//#region src/__generated__/internal/application/client/getVersions.ts
/**
* @summary Получить историю версий итогового решения
* {@link /api/mdm/internal/api/internal/requests/:requestId/final-decision/versions}
*/
function getVersions(options) {
	const { client: request = client, ...config } = options;
	return request({
		method: "GET",
		url: "/api/mdm/internal/api/internal/requests/{requestId}/final-decision/versions",
		validator: { response: getVersionsResponseSchema },
		...config
	});
}
//#endregion
//#region src/__generated__/internal/application/client/importRequest.ts
/**
* @description Загружает ZIP-архив (одна или несколько заявок в папках request_<id>/) из внешнего контура. Создаёт новые и обновляет существующие заявки, возвращает сводку. Некорректные заявки пропускаются.
* @summary Импорт ZIP-архива заявок (мультизаявочный)
* {@link /api/mdm/internal/api/internal/requests/import}
*/
function importRequest(options) {
	const { client: request = client, ...config } = options;
	return request({
		method: "POST",
		url: "/api/mdm/internal/api/internal/requests/import",
		validator: { response: importRequestResponseSchema },
		contentType: { request: "multipart/form-data" },
		...config
	});
}
//#endregion
//#region src/__generated__/internal/application/client/list.ts
/**
* @summary Получить все строки частей ТУ заявки
* {@link /api/mdm/internal/api/internal/requests/:requestId/tu-parts}
*/
function list(options) {
	const { client: request = client, ...config } = options;
	return request({
		method: "GET",
		url: "/api/mdm/internal/api/internal/requests/{requestId}/tu-parts",
		validator: { response: listResponseSchema },
		...config
	});
}
//#endregion
//#region src/__generated__/internal/application/client/listAttachmentDescriptions.ts
/**
* @description         Список {fileId, description}. Доступ как у detail: OPERATOR/MONITORING — любую;
* SECRETARY_NG и APPARATUS_NG — свою дорогу; NTP_HEAD — свои назначения или свою дорогу;
* NTP_RESPONSIBLE и прочие — только при наличии назначения на заявке.
* @summary Описания вложений заявки
* {@link /api/mdm/internal/api/internal/requests/:id/attachments}
*/
function listAttachmentDescriptions(options) {
	const { client: request = client, ...config } = options;
	return request({
		method: "GET",
		url: "/api/mdm/internal/api/internal/requests/{id}/attachments",
		validator: { response: listAttachmentDescriptionsResponseSchema },
		...config
	});
}
//#endregion
//#region src/__generated__/internal/application/client/setAttachmentDescription.ts
/**
* @description Upsert описания файла. Только OPERATOR, запрещено если заявка финализирована.
* @summary Установить описание вложения
* {@link /api/mdm/internal/api/internal/requests/:id/attachments/:fileId}
*/
function setAttachmentDescription(options) {
	const { client: request = client, ...config } = options;
	return request({
		method: "PUT",
		url: "/api/mdm/internal/api/internal/requests/{id}/attachments/{fileId}",
		validator: { response: setAttachmentDescriptionResponseSchema },
		...config
	});
}
//#endregion
//#region src/__generated__/internal/application/client/transition.ts
/**
* @description         Выполняет действие-триггер перехода жизненного цикла заявки (STORY-975).
* Допустимость перехода и требуемая роль инициатора определяются матрицей
* переходов; действует жёсткий gating по паре (статус × роль) — недопустимое
* действие или отсутствие роли завершаются ошибкой 409. Если у текущего статуса
* есть слот-держатель, действие выполняет только он; переназначить держателя без
* смены статуса можно через `PUT /assignments/{slot}`, а если слот текущего
* статуса ещё не занят — ответ 409 `ASSIGNMENT_NOT_FOUND`. Для статусов без
* слота (`INTAKE_CHECK`, `REVISION_REQUIRED`) держателя нет — достаточно роли
* и скоупа доступа.
* Тело запроса — `TransitionCommand{comment, resultFileId, assigneeId, documentIds}`.
* `comment` — текст замечаний/отказа, `resultFileId` — опциональный id файла
* результата (ТУ), привязанного к заявке. Для `ntp-rework`/`ng-rework` обязателен непустой
* `comment`, а `documentIds` содержит опциональные документы замечания.
* `assigneeId` — исполнитель, назначаемый в слот:
* обязателен для действий, заполняющих слот (`intake-pass`, `send-to-ntp`,
* `assign-ntp-resp`, отмечены в таблице ниже) — без него 409
* `ASSIGNEE_REQUIRED`; роль назначаемого проверяется в Keycloak — пользователь
* не найден даёт `ASSIGNEE_NOT_FOUND`, несоответствие роли слоту —
* `ASSIGNEE_ROLE_MISMATCH`. Для остальных действий тело опционально.
* Перечень действий (`action` в пути):
* | action | Переход статуса | Роль | Исполнитель | За что отвечает |
* |---|---|---|---|---|
* | `intake-pass` | INTAKE_CHECK → SENT_TO_NG | Секретарь НГ | обязателен | Входной контроль пройден, заявка направлена в аппарат НГ |
* | `request-revision` | INTAKE_CHECK / IN_NG → REVISION_REQUIRED | Секретарь НГ / Аппарат НГ | — | Возврат заявителю на доработку; сохраняет точку возврата (return_stage) и `comment` с замечаниями |
* | `reject` | INTAKE_CHECK / IN_NG / NG_APPROVAL → REJECTED | Секретарь НГ / Аппарат НГ | — | Мотивированный отказ (терминальный); обязателен `comment` — причина, опциональны `documentIds` |
* | `take-ng` | SENT_TO_NG → IN_NG | Аппарат НГ | — | Взятие заявки в работу аппаратом НГ |
* | `send-to-ntp` | IN_NG → SENT_TO_NTP | Аппарат НГ | обязателен | Направление заявки в НТП |
* | `take-ntp` | SENT_TO_NTP → IN_NTP | Начальник НТП | — | Взятие заявки в работу в НТП |
* | `assign-ntp-resp` | IN_NTP → NTP_RESP_ASSIGNED | Начальник НТП | обязателен | Назначение ответственного исполнителя в НТП |
* | `start-commission-prep` | NTP_RESP_ASSIGNED → COMMISSION_PREP | Ответственный НТП | — | Старт подготовки комиссии |
* | `start-decision` | COMMISSION_PREP → DECISION_FORMING | Ответственный НТП | — | Переход к формированию решения |
* | `submit-ntp-approval` | DECISION_FORMING → NTP_APPROVAL | Ответственный НТП | — | Отправка сформированного решения на согласование НТП |
* | `ntp-rework` | NTP_APPROVAL → DECISION_REWORK | Начальник НТП | — | Immutable `RETURNED` + замечание; обязательны `comment`, опциональны `documentIds` |
* | `ntp-approve` | NTP_APPROVAL → NG_APPROVAL | Начальник НТП | — | Immutable `APPROVED`; решение передано на согласование НГ |
* | `ng-rework` | NG_APPROVAL → DECISION_REWORK | Аппарат НГ | — | Immutable `RETURNED` + замечание; обязательны `comment`, опциональны `documentIds` |
* | `ng-approve` | NG_APPROVAL → TU_ISSUED / REJECTED | Аппарат НГ | — | Immutable `APPROVED`; terminal status берётся из decision текущей версии, `resultFileId` опционален |
* | `rework-resubmit` | DECISION_REWORK → NTP_APPROVAL / NG_APPROVAL | Ответственный НТП | — | Создаёт следующую immutable версию и возвращает на этап последнего `RETURNED` |
* `take-ng` и `take-ntp` — «авто»-действия (взятие в работу), остальные инициируются вручную.
* @summary Выполнить переход статуса заявки
* {@link /api/mdm/internal/api/internal/requests/:requestId/transitions/:action}
*/
function transition(options) {
	const { client: request = client, ...config } = options;
	return request({
		method: "POST",
		url: "/api/mdm/internal/api/internal/requests/{requestId}/transitions/{action}",
		validator: { response: transitionResponseSchema },
		...config
	});
}
//#endregion
//#region src/__generated__/internal/application/client/unlink.ts
/**
* @summary Отвязать актуальную часть ТУ
* {@link /api/mdm/internal/api/internal/requests/:requestId/tu-parts/:fileId}
*/
function unlink(options) {
	const { client: request = client, ...config } = options;
	return request({
		method: "DELETE",
		url: "/api/mdm/internal/api/internal/requests/{requestId}/tu-parts/{fileId}",
		validator: { response: unlinkResponseSchema },
		...config
	});
}
//#endregion
//#region src/__generated__/internal/application/client/update.ts
/**
* @summary Изменить черновик итогового решения
* {@link /api/mdm/internal/api/internal/requests/:requestId/final-decision}
*/
function update(options) {
	const { client: request = client, ...config } = options;
	return request({
		method: "PUT",
		url: "/api/mdm/internal/api/internal/requests/{requestId}/final-decision",
		validator: { response: updateResponseSchema },
		...config
	});
}
//#endregion
export { toEventStream as A, bind as C, createClientCore as D, createClient as E, defaultPathSerializer as F, defaultQuerySerializer as I, isDefaultJsonBody as L, validateStandardSchema as M, applyHeaderStyles as N, parseEventStream as O, defaultBodySerializer as P, serializeCookies as R, create as S, client as T, get2 as _, listAttachmentDescriptions as a, deleteAttachmentDescription as b, getVersions as c, getRefusal as d, getNeedsAttention as f, getCounts as g, getCurrentVersion as h, setAttachmentDescription as i, ParseError as j, resolveAuth as k, getRequests as l, getFilterValues as m, unlink as n, list as o, getHistory as p, transition as r, importRequest as s, update as t, getRequest as u, exportRequests as v, ResponseError as w, createRemark as x, deleteRemark as y };

//# sourceMappingURL=update.js.map